import React, { useMemo, useEffect } from 'react'
import RichTextEditor from 'reactjs-tiptap-editor';
import { BaseKit } from 'reactjs-tiptap-editor';
import { Blockquote } from 'reactjs-tiptap-editor/blockquote';
import { Bold } from 'reactjs-tiptap-editor/bold';
import { Clear } from 'reactjs-tiptap-editor/clear';
import { Color } from 'reactjs-tiptap-editor/color';
import { FontFamily } from 'reactjs-tiptap-editor/fontfamily';
import { FontSize } from 'reactjs-tiptap-editor/fontsize';
import { FormatPainter } from 'reactjs-tiptap-editor/formatpainter';
import { Heading } from 'reactjs-tiptap-editor/heading';
import { Highlight } from 'reactjs-tiptap-editor/highlight';
import { History } from 'reactjs-tiptap-editor/history';
import { HorizontalRule } from 'reactjs-tiptap-editor/horizontalrule';
import { Image } from 'reactjs-tiptap-editor/image';
import { Indent } from 'reactjs-tiptap-editor/indent';
import { Italic } from 'reactjs-tiptap-editor/italic';
import { LineHeight } from 'reactjs-tiptap-editor/lineheight';
import { Link } from 'reactjs-tiptap-editor/link';
import { TaskList } from 'reactjs-tiptap-editor/tasklist';
import { BulletList } from 'reactjs-tiptap-editor/bulletlist';
import { ListItem } from 'reactjs-tiptap-editor/listitem';
import { OrderedList } from 'reactjs-tiptap-editor/orderedlist';
import { MoreMark } from 'reactjs-tiptap-editor/moremark';
import { ColumnActionButton } from 'reactjs-tiptap-editor/multicolumn';
import { Selection } from 'reactjs-tiptap-editor/selection';
import { SlashCommand } from 'reactjs-tiptap-editor/slashcommand';
import { Strike } from 'reactjs-tiptap-editor/strike';
import { TextUnderline } from 'reactjs-tiptap-editor/textunderline';
import { Table } from 'reactjs-tiptap-editor/table';
import { TextAlign } from 'reactjs-tiptap-editor/textalign';
import { TextBubble } from 'reactjs-tiptap-editor/textbubble';
import { SearchAndReplace } from 'reactjs-tiptap-editor/searchandreplace';
import { ExportPdf } from 'reactjs-tiptap-editor/exportpdf';
import { ImportWord } from 'reactjs-tiptap-editor/importword';
import { ExportWord } from 'reactjs-tiptap-editor/exportword';
import { TableOfContents } from 'reactjs-tiptap-editor/tableofcontent';
import { TextDirection } from 'reactjs-tiptap-editor/textdirection';
import { Attachment } from 'reactjs-tiptap-editor/attachment';
import { useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import 'reactjs-tiptap-editor/style.css';
import 'react-image-crop/dist/ReactCrop.css';
import "react-loading-skeleton/dist/skeleton.css";
import './RichTextEditor.scss'
import { sanitizeHtml } from '../../helpers/utils';
import { ref } from 'process';
import { api as attachmentService } from '../../store/services/attachments.service';
import { useDispatch } from 'react-redux';
import { generalActions } from "../../store/actions";

export default (props) => {
  const editorRef = useRef(null);
  const hasMounted = useRef(false);
  const [contentCount, setContentCount] = useState(0);
  const {
    mode = "base",
    output = "html",
    onChange,
    darkMode = false,
    content,
    limit,
    disabled = false,
    label,
    maxWidth,
    minHeight,
    maxHeight = "26rem",
    wrapperClass,
    contentClass,
    loading = false,
    fileUploadParams = { enabled: false, refType: null, refId: null },
    required = false,
    errorMessage,
  } = props;
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB default
  const dispatch = useDispatch();

  const sanitizedContent = useMemo(() => sanitizeHtml(content), [content]);

  const handleAttachmentUpload = async (file) => {
    try {

      if (file.size > MAX_FILE_SIZE) {
        dispatch(
          generalActions.showToast({
            title: `File size exceeds ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB limit`,
            type: "error",
          })
        )
        return null;
      }

      // Create attachment
      const attachmentData = {
        title: file.name,
        refType: fileUploadParams.refType,
        refId: fileUploadParams.refId,
        file: file,
      };

      var response = await attachmentService.createAttachment(attachmentData, file);
      return response?.url;
    } catch (error) {
      console.error('Attachment upload failed:', error);
      return null;
    }
  };

  const extensions = [
    BaseKit.configure({
      listItem: false,
      selection: false,
      textBubble: false,
      characterCount: {
        limit,
        mode: "nodeSize",
      },
    }),
    History.configure({ toolbar: mode === "advanced" }),

    // === Text Styling ===
    mode === "advanced" && FontSize.configure({ spacer: true }),
    //FontFamily,
    mode === "advanced" && Heading,
    Bold,
    Italic,
    Strike,
    TextUnderline,
    Highlight,
    Color,
    mode === "advanced" && FormatPainter,
    Clear,

    // === Block Elements ===
    mode === "advanced" && HorizontalRule.configure({ spacer: true }),
    mode === "advanced" ? Blockquote : Blockquote.configure({ spacer: true }),

    // === Lists ===
    BulletList,
    OrderedList,
    TaskList,
    ListItem,
    Link,
    mode === "advanced" && LineHeight.configure({ spacer: true }),
    mode === "advanced" ? Indent : Indent.configure({ spacer: true }),
    TextAlign,
    mode === "advanced" && TextDirection,

    // === Collaboration & Productivity ===
    Selection,
    SlashCommand,
    TextBubble,

    // === Tables ===
    ColumnActionButton.configure({ spacer: true }),
    Table,

    // === Advanced ===
    mode === "advanced" && SearchAndReplace.configure({ spacer: true }),
    mode === "advanced" && fileUploadParams.enabled &&
    Image.configure({
      upload: handleAttachmentUpload,
      resourceImage: 'upload',
      defaultInline: true,
    }),
    // mode === "advanced" && fileUploadParams.enabled &&
    // Attachment.configure({
    //   upload: handleAttachmentUpload,
    // }),
    mode === "advanced" && ImportWord,
    // mode === "advanced" && ExportPdf,
    // mode === "advanced" && ExportWord,
    // mode === "advanced" && TableOfContents,
  ].filter(Boolean);

  const handleChange = (value) => {
    const editor = editorRef?.current?.editor;
    if (!editor) return;

    const count = editor.storage?.characterCount?.characters || 0;
    let currentValue = editor.getHTML();
    if (output === "html" && /^<p>\s*<\/p>$/.test(currentValue)) {
      value = "";
    }

    setContentCount(count);
    if (hasMounted.current && onChange) {
      onChange(sanitizeHtml(value));
    }
  }

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const handleTransaction = ({ transaction }) => {
    const extractAssets = (doc) => {
      const images = [];
      const attachments = [];

      doc.descendants((node) => {
        // Images
        if (node.type?.name === "image" && node.attrs?.src) {
          images.push(node.attrs.src);
        }

        // Attachments
        // if (node.type?.name === "attachment" && node.attrs?.url) {
        //   attachments.push(node.attrs.url);
        // }
      });

      return { images, attachments };
    };

    const before = extractAssets(transaction.before);
    const after = extractAssets(transaction.doc);

    // Deleted Images
    const deletedImages = before.images.filter(
      (src) => !after.images.includes(src)
    );

    // Deleted Attachments
    // const deletedAttachments = before.attachments.filter(
    //   (url) => !after.attachments.includes(url)
    // );

    // HANDLE DELETE
    deletedImages.forEach(async (src) => {
      if (src) {
        try {
          await attachmentService.deleteAttachment(src);
        } catch { }
      }
    });
  };

  return (
    <div className={`rich-text-editor ${wrapperClass || ''}`}>
      {label && <label className="editor-label">
        {label} {required && <span className="Input_staric">*</span>}
      </label>}
      {loading ?
        (
          <Skeleton height={mode === "advanced" ? 'calc(100vh - 417px)' : 120} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
        ) :
        (
          <>
            <RichTextEditor
              ref={editorRef}
              contentClass={`richtext-inner-editor ${contentClass}`}
              output={output}
              dark={darkMode}
              content={sanitizedContent}
              extensions={extensions}
              disabled={disabled}
              maxWidth={maxWidth}
              minHeight={minHeight}
              maxHeight={maxHeight}
              onChangeContent={handleChange}
              useEditorOptions={{
                onTransaction: handleTransaction,
              }}
            />
            {limit && (
              <span
                className="d-flex justify-content-end"
                style={{ fontSize: "11px" }}>
                {`${contentCount || 0}/${limit}`}</span>
            )}
            {errorMessage && (
              <p className="Input_errorMessage">
                {errorMessage}
              </p>
            )}
          </>
        )
      }

    </div>

  )
}